import { useState, useEffect, useCallback, memo } from "react";
import styled, { keyframes } from "styled-components";
import UploadBox from "./UploadBox";
import {
  getCurrentMarkerObj,
  isValidMarkerId,
  updateCurrentMarkerObj,
  createMarkerObj,
  deleteImage,
  uploadImage,
  removePhotoCurrentMarkerObj,
  debounce,
  getTranslation,
  getCategoriesForSaving,
} from "./Utility.js";
import toast from "react-hot-toast";
import { t } from "@lingui/core/macro";
import { ProgressBar } from "react-loader-spinner";

const jump = keyframes`
  from{
    transform: translateY(0)
  }
  to{
    transform: translateY(-3px)
  }
`;

const Button = styled.button`
  max-width: fit-content;
  margin: 0 auto;
  padding: 11px 13px;
  color: rgb(253, 249, 243);
  font-weight: 600;
  text-transform: uppercase;
  background: #f03d4e;
  border: none;
  border-radius: 3px;
  outline: 0;
  cursor: pointer;
  margin-top: 0.6rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease-out;
  &:hover {
    background-color: rgb(93, 25, 34);
    animation: ${jump} 0.2s ease-out forwards;
  }
`;

const Input = styled.input`
  max-width: 100%;
  padding: 11px 13px;
  background: #f9f9fa;
  color: #f03d4e;
  margin-bottom: 0.9rem;
  border-radius: 4px;
  outline: 0;
  border: 1px solid rgba(245, 245, 245, 0.7);
  font-size: 14px;
  transition: all 0.3s ease-out;
  @media (min-width: 768px) {
    width: 70vh;
    flex-direction: row;
    justify-content: space-between;
  }
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.1), 0 1px 1px rgba(0, 0, 0, 0.1);
  :focus,
  :hover {
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.15), 0 1px 5px rgba(0, 0, 0, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  font-size: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  margin-bottom: 1rem;
  background: papayawhip;
  @media (min-width: 768px) {
    width: 30vh;
    flex-direction: row;
    justify-content: space-between;
  }
`;

/**
 * A component that displays an editing story form
 *
 * @param {object} props
 * @returns {JSX.Element}
 */
const DrawerContent = memo((options) => {
  const [data, setData] = useState(false);
  const [locked, setLocked] = useState(false);
  const [temporaryDescription, setTemporaryDescription] = useState("");
  const [temporaryImage, setTemporaryImage] = useState(null);
  const [temporaryImageList, setTemporaryImageList] = useState([]);
  const [temporaryName, setTemporaryName] = useState("");
  const [temporaryCategory, setTemporaryCategory] = useState(null);

  /**
   * A function that get a story from remote API
   * @param {string} id - A story ID
   * @returns {void} Set state variables
   */
  const getData = (id) => {
    getCurrentMarkerObj(id)
      .then((data) => {
        console.log(data);
        if (data.success) {
          setData({
            id: data.data._id,
            photos: data.data.photos,
            description: data.data.descriptionGive,
            position: data.data.position,
            w3wData: data.data.w3wData,
            fullAddress: data.data.fullAddress,
            city: data.data.city,
            province: data.data.province,
            country: data.data.country,
            name: data.data.name,
            website: data.data.website,
            category: data.data.category,
            businessHours: data.data.businessHours,
          });
        }
        setTemporaryDescription(data.data.descriptionGive);
        setTemporaryName(data.data.name);
        setTemporaryCategory(data.data.category);
      })
      .catch((error) => console.error(error));
  };

  /**
   * A function that asks for confirmation before saving a story
   * @returns {void} Call an option function to close the drawer
   */
  const handleCancel = () => {
    if (!window.confirm(t`Are you sure you don't want to save this story?`)) {
      return false;
    }
    options.closeAll();
  };

  /**
   * A function that manages a story updating
   * @returns {void} Return a boolean or call an another function
   */
  const handleSubmissionUpdate = () => {
    // Check that at least a photo has been inserted
    if (data.photos.length === 0 && temporaryImageList.length === 0) {
      toast(t`To save your story, you must upload at least one image!`, {
        icon: "👋",
      });
      return false;
    }

    // Check that at least a name has been inserted  (min. 3 chars)
    if (!temporaryName || temporaryName.length < 3) {
      toast(t`To save your story, you must enter at least a name!`, {
        icon: "👋",
      });
      return false;
    }

    // Check that at least a category has been chosen
    if (!temporaryCategory) {
      toast(t`To save your story, you must upload at least a category!`, {
        icon: "👋",
      });
      return false;
    }

    // Check that at least a description has been inserted (min. 120 chars)
    if (temporaryDescription.length < 120) {
      toast(t`To save your story, you must enter a description!`, {
        icon: "👋",
      });
      return false;
    }

    saveAllDataStory();
  };

  /**
   * A function that manages a story saving
   * @returns {void} Return a boolean or call an another function
   */
  // Check that all the data needed to save the story has been entered
  const handleSubmission = useCallback(() => {
    // Check that at least a photo has been inserted
    if (temporaryImageList.length === 0) {
      toast(t`To save your story, you must upload at least one image!`, {
        icon: "👋",
      });
      return false;
    }

    // Check that at least a name has been inserted  (min. 3 chars)
    if (!temporaryName || temporaryName.length < 3) {
      toast(t`To save your story, you must enter at least a name!`, {
        icon: "👋",
      });
      return false;
    }

    // Check that at least a category has been chosen
    if (!temporaryCategory) {
      toast(t`To save your story, you must upload at least a category!`, {
        icon: "👋",
      });
      return false;
    }

    // Check that at least a description has been inserted (min. 120 chars)
    if (temporaryDescription.length < 120) {
      toast(t`To save your story, you must enter a description!`, {
        icon: "👋",
      });
      return false;
    }

    saveAllDataStory();
  }, [
    temporaryName,
    temporaryCategory,
    temporaryImageList,
    temporaryDescription,
  ]);

  /**
   * A function that save a story
   * @returns {void} Call a save API and set variables of state
   */
  const saveAllDataStory = async () => {
    // For a new story to save
    if (
      options &&
      !options.markerInUse.id &&
      !isValidMarkerId(options.markerInUse.id) &&
      !isValidMarkerId(data.id)
    ) {
      // Prepare list of images to save
      const imageList = temporaryImageList.map((image, index) => {
        return { url: image.secure_url, publicID: image.public_id };
      });

      const storyObjCreate = {
        photos: imageList,
        description: temporaryDescription,
        name: temporaryName,
        category: temporaryCategory,
        position: {
          lat: options.markerInUse.lat,
          lng: options.markerInUse.lng,
        },
        w3wData: options.markerInUse.w3wData ? options.markerInUse.w3wData : {},
      };

      const newMarkerResponse = await createMarkerObj(storyObjCreate);
      if (newMarkerResponse.success) {
        setData({
          id: newMarkerResponse.data._id,
          photos: newMarkerResponse.data.photos,
          name: newMarkerResponse.data.name,
          category: newMarkerResponse.data.category,
          description: newMarkerResponse.data.descriptionGive,
        });

        toast(t`Great, you've added a new story to the map!`, {
          icon: "👋",
        });
        options.closeAll();
      }
      if (newMarkerResponse.success === false) {
        toast(t`Problem creating story`, {
          icon: "🥵",
        });
      }
    }

    // For an existing story to update
    if (
      options &&
      options.markerInUse &&
      (isValidMarkerId(options.markerInUse.id) || isValidMarkerId(data.id))
    ) {
      const markerID = options.markerInUse.id || data.id;

      // Prepare list of images to save
      const imageList = temporaryImageList.map((image, index) => {
        return { url: image.secure_url, publicID: image.public_id };
      });

      const updatedImageList = data.photos
        ? [...data.photos, ...imageList]
        : imageList;
      const storyObjUpdate = {
        id: markerID,
        photos: updatedImageList,
        description: temporaryDescription,
        name: temporaryName,
        category: temporaryCategory,
        position: {
          lat: options.markerInUse.lat,
          lng: options.markerInUse.lng,
        },
        w3wData: options.markerInUse.w3wData ? options.markerInUse.w3wData : {},
      };

      const updatedMarkerResponse = await updateCurrentMarkerObj(
        storyObjUpdate
      );
      // Update current layout with marker from API

      if (updatedMarkerResponse.success) {
        setData({
          id: updatedMarkerResponse.data._id,
          photos: updatedMarkerResponse.data.photos,
          name: updatedMarkerResponse.data.name,
          category: updatedMarkerResponse.data.category,
          description: updatedMarkerResponse.data.descriptionGive,
        });

        toast(t`Great! You have successfully updated your story!`, {
          icon: "👋",
        });

        options.closeAll();
      }
      if (updatedMarkerResponse.success === false) {
        toast(t`Problems updating story images`, {
          icon: "🥵",
        });
      }
    }
  };

  /**
   * A function that update temporary image list in case of a new uploaded image
   * @param {object} imageFile - An image file data
   * @returns {object} Set variables of state and return an object or null
   */
  const updateTemporaryImageEventHandler = async (imageFile) => {
    if (imageFile) {
      setLocked(true);
      //update image in pre-existing markerobj or create a new markerobj
      const imageUploaded = await uploadImage(imageFile);
      setTemporaryImageList([...temporaryImageList, imageUploaded]);
      setLocked(false);
      return imageFile;
    }
    return null;
  };

  /**
   * A function that update images for a story
   * @param {object} imageFile - A story image
   * @returns {void} Create a story with a new image or update an existing one
   */
  const updateImageEventHandler = async (imageFile) => {
    if (imageFile) {
      setLocked(true);
      // Update image in pre-existing markerobj or create a new markerobj
      const imageUploaded = await uploadImage(imageFile);
      // Now create a new marker or updated an existing already created or in updating
      if (
        options &&
        !options.markerInUse.id &&
        !isValidMarkerId(options.markerInUse.id) &&
        !isValidMarkerId(data.id)
      ) {
        // check for description presence in case of a new story obj
        if (temporaryDescription.length < 120) {
          setTemporaryImage(imageUploaded);
          toast(t`To save your story, you must enter a description!`, {
            icon: "👋",
          });
          setLocked(false);
          return imageFile;
        }

        const storyObjCreate = {
          photos: [
            {
              url: imageUploaded.secure_url,
              publicID: imageUploaded.public_id,
            },
          ],
          description: temporaryDescription,
          position: {
            lat: options.markerInUse.lat,
            lng: options.markerInUse.lng,
          },
          w3wData: options.markerInUse.w3wData
            ? options.markerInUse.w3wData
            : {
                address: "home/my/dear",
                idW: "home/my/dear",
              },
        };

        const newMarkerResponse = await createMarkerObj(storyObjCreate);
        if (newMarkerResponse.success) {
          setData({
            id: newMarkerResponse.data._id,
            photos: newMarkerResponse.data.photos,
            description: newMarkerResponse.data.descriptionGive,
          });

          toast(t`Great, you've added a new story to the map!`, {
            icon: "👋",
          });
        }
        if (newMarkerResponse.success === false) {
          toast(t`Problems in creating story with image`, {
            icon: "🥵",
          });
        }
        console.log();
        setLocked(false);
      }

      if (
        options &&
        options.markerInUse &&
        (isValidMarkerId(options.markerInUse.id) || isValidMarkerId(data.id))
      ) {
        const markerID = options.markerInUse.id || data.id;
        const descriptionValue = data.description || "";
        const photosValue = data.photos
          ? [
              ...data.photos,
              {
                url: imageUploaded.secure_url,
                publicID: imageUploaded.public_id,
              },
            ]
          : [
              {
                url: imageUploaded.secure_url,
                publicID: imageUploaded.public_id,
              },
            ];
        const storyObjUpdate = {
          id: markerID,
          description: descriptionValue,
          photos: photosValue,
          position: {
            lat: options.markerInUse.lat,
            lng: options.markerInUse.lng,
          },
          w3wData: options.markerInUse.w3wData
            ? options.markerInUse.w3wData
            : {
                address: "home/my/dear",
                idW: "home/my/dear",
              },
        };
        const updatedMarkerResponse = await updateCurrentMarkerObj(
          storyObjUpdate
        );

        if (updatedMarkerResponse.success) {
          setData({
            id: updatedMarkerResponse.data._id,
            photos: updatedMarkerResponse.data.photos,
            description: updatedMarkerResponse.data.descriptionGive,
          });

          toast(t`Great! You have successfully updated your story!`, {
            icon: "👋",
          });
        }
        if (updatedMarkerResponse.success === false) {
          toast(t`Problems updating story images`, {
            icon: "🥵",
          });
        }
        setLocked(false);
      }

      return imageUploaded;
    }
  };

  /**
   * A function that manages a story image deletion
   * @param {string} publicId - The cloud id image
   * @param {string} imageID - The story image id
   * @returns {void} Call api for cloud deletion and next an api for story update
   */
  const deleteImageEventHandler = async (publicId, imageID) => {
    if (publicId) {
      setLocked(true);

      const imageDeleted = isValidMarkerId(publicId)
        ? await deleteImage(publicId)
        : null;
      // For a temporary image removed it from temporary image list
      if (!imageID) {
        const purgedTemporaryImageList = temporaryImageList.filter((value) => {
          return value.public_id !== publicId;
        });
        setTemporaryImageList(purgedTemporaryImageList);
      }

      // For an image saved on db remove it from db
      if (
        isValidMarkerId(imageID) &&
        options &&
        options.markerInUse &&
        (isValidMarkerId(options.markerInUse.id) || isValidMarkerId(data.id))
      ) {
        const markerID = options.markerInUse.id || data.id;

        const updatedMarkerResponse = await removePhotoCurrentMarkerObj(
          markerID,
          imageID
        );
        if (updatedMarkerResponse.success) {
          setData({
            id: updatedMarkerResponse.data._id,
            photos: updatedMarkerResponse.data.photos,
            name: updatedMarkerResponse.data.name,
            category: updatedMarkerResponse.data.category,
            description: updatedMarkerResponse.data.descriptionGive,
          });
          toast(t`Great! Photo successfully removed!`, {
            icon: "👋",
          });
        }
        if (updatedMarkerResponse.success === false) {
          toast(t`Problems removing story image`, {
            icon: "🥵",
          });
        }
      }

      setLocked(false);
      return imageDeleted;
    }
  };

  /**
   * A function to add/update a story description
   * @param {object} event - An event text typing
   * @returns {void} Set a variable of state
   */
  const updateDescriptionTypedEventHandler = async (event) => {
    const descriptionValue =
      event.target.value.trim().length === 0 ? "" : event.target.value;
    setTemporaryDescription(descriptionValue);
  };

  /**
   * A function to create/update a story with a new description
   * @param {object} event - An event text typing
   * @returns {void} Create a story with a new description or update an existing one
   */
  const updateDescriptionEventHandler = async (event) => {
    const descriptionValue =
      event.target.value.trim().length === 0 ? "" : event.target.value;
    if (descriptionValue.length < 120) {
      return;
    }
    setTemporaryDescription(descriptionValue);
    if (
      options &&
      options.markerInUse &&
      (isValidMarkerId(options.markerInUse.id) || isValidMarkerId(data.id))
    ) {
      const markerID = options.markerInUse.id || data.id;
      const photosValue = data.photos ? data.photos : undefined;

      const storyObjUpdate = {
        id: markerID,
        description: descriptionValue,
        photos: photosValue,
        position: {
          lat: options.markerInUse.lat,
          lng: options.markerInUse.lng,
        },
        w3wData: options.markerInUse.w3wData
          ? options.markerInUse.w3wData
          : {
              address: "home/my/dear",
              idW: "home/my/dear",
            },
      };

      const updatedMarkerResponse = await updateCurrentMarkerObj(
        storyObjUpdate
      );
      // Update current layout with marker from API

      if (updatedMarkerResponse.success) {
        setData({
          id: updatedMarkerResponse.data._id,
          photos: updatedMarkerResponse.data.photos,
          description: updatedMarkerResponse.data.descriptionGive,
        });
        if (photosValue.length) {
          toast(t`Great! You have successfully updated the description`, {
            icon: "👋",
          });
        } else {
          toast(t`To save your story, you must upload at least one image!`, {
            icon: "👋",
          });
        }
      }
      if (updatedMarkerResponse.success === false) {
        toast(t`Problems updating history`, {
          icon: "🥵",
        });
      }
    }
    if (
      options &&
      !options.markerInUse.id &&
      !isValidMarkerId(options.markerInUse.id) &&
      !isValidMarkerId(data.id)
    ) {
      // Check for image presence in case of a new story obj
      if (!temporaryImage) {
        toast(t`To save your story, you must upload at least one image!`, {
          icon: "👋",
        });
        return;
      }

      const storyObjCreate = {
        photos: [
          {
            url: temporaryImage.secure_url,
            publicID: temporaryImage.public_id,
          },
        ],

        description: descriptionValue,
        position: {
          lat: options.markerInUse.lat,
          lng: options.markerInUse.lng,
        },
        w3wData: options.markerInUse.w3wData
          ? options.markerInUse.w3wData
          : {
              address: "home/my/dear",
              idW: "home/my/dear",
            },
      };
      const newMarkerResponse = await createMarkerObj(storyObjCreate);

      if (newMarkerResponse.success) {
        setData({
          id: newMarkerResponse.data._id,
          photos: newMarkerResponse.data.photos,
          description: newMarkerResponse.data.descriptionGive,
        });

        toast(t`To save your story, you must upload at least one image!`, {
          icon: "👋",
        });
      }
      if (newMarkerResponse.success === false) {
        toast(t`Problems creating story with description`, {
          icon: "🥵",
        });
      }
    }
  };

  /**
   * A function that manages the UI image data
   * @returns {void} Save all information in state variables
   */
  const buildListImagesForUploader = () => {
    let savedPhotos = [];
    if (data && data.photos) {
      savedPhotos = data.photos.map((aPhoto, index) => {
        return {
          photo: aPhoto,
          data_url: aPhoto.url,
          publicId: aPhoto.publicID,
          id: aPhoto._id,
          w3wText: options.markerInUse.w3wData.address,
        };
      });
    }
    const temporaryPhotos = temporaryImageList.map((aPhoto, index) => {
      return {
        photo: aPhoto,
        data_url: aPhoto.secure_url,
        publicId: aPhoto.public_id,
        id: null,
        w3wText: options.markerInUse.w3wData.address,
      };
    });
    const allPhotos = [...savedPhotos, ...temporaryPhotos];
    return allPhotos;
  };
  let optimisedHandleChange = debounce(
    updateDescriptionTypedEventHandler,
    1000
  );

  useEffect(() => {
    console.log();
  }, []);

  //
  useEffect(() => {
    // check if the clicked marker is a pre-existing and valid marker
    if (
      options &&
      options.markerInUse &&
      isValidMarkerId(options.markerInUse.id) &&
      !isValidMarkerId(data.id)
    ) {
      getData(options.markerInUse.id);
      console.log("----- " + options.markerInUse);
    }
    // if the clicked marker is editable (because your are the owner) then
    // set true the editable state
    // eslint-disable-next-line
  }, [options]);

  return (
    <>
      <div className="uploadBoxList">
        {console.log("Drawercontent re-renderizzato")}
        {!data && !isValidMarkerId(options.markerInUse.id) ? (
          <>
            <UploadBox
              disabled={locked}
              onUploadedImage={updateTemporaryImageEventHandler}
              onDeletedImage={deleteImageEventHandler}
              images={buildListImagesForUploader}
            >
              {" "}
            </UploadBox>
            <ProgressBar
              visible={locked}
              height="40"
              width="80%"
              color="#4fa94d"
              ariaLabel="progress-bar-loading"
              wrapperStyle={{ margin: "0 auto" }}
              wrapperClass=""
            />
            <div className="uploadGroupElement">
              <Input
                id="name"
                type="text"
                value={temporaryName}
                placeholder={t`Enter a name`}
                onChange={(e) => setTemporaryName(e.target.value)}
                required
                disabled={locked}
              />
              <Select
                id="category"
                value={temporaryCategory}
                onChange={(e) => setTemporaryCategory(e.target.value)}
                required
                disabled={locked}
              >
                {getCategoriesForSaving().map((category, index) => (
                  <option
                    key={category}
                    value={
                      category === getCategoriesForSaving()[0] ? "" : category
                    } // Se è il placeholder, imposta il valore a stringa vuota
                    disabled={locked} // Disabilita il placeholder
                  >
                    {getTranslation(category)}
                  </option>
                ))}
              </Select>
            </div>

            <progress
              style={{
                width: "98%",
                "accent-color":
                  temporaryDescription.length >= 120 ? "green" : "red",
              }}
              value={temporaryDescription.length / 120}
            >
              {temporaryDescription.length}
            </progress>
            <textarea
              placeholder={t`Describe this area`}
              disabled={locked}
              className="story-description"
              onChange={optimisedHandleChange}
            ></textarea>
            <div className="uploadSaveElement">
              <Button onClick={() => handleSubmission()}>{t`Save`}</Button>
              <Button onClick={() => handleCancel()}>{t`Cancel`}</Button>
            </div>
          </>
        ) : (
          data &&
          data.photos && (
            <>
              <UploadBox
                disabled={locked}
                onUploadedImage={updateTemporaryImageEventHandler}
                onDeletedImage={deleteImageEventHandler}
                images={buildListImagesForUploader}
              >
                {" "}
              </UploadBox>
              <ProgressBar
                visible={locked}
                height="40"
                width="80%"
                color="#4fa94d"
                ariaLabel="progress-bar-loading"
                wrapperStyle={{ margin: "0 auto" }}
                wrapperClass=""
              />
              <div className="uploadGroupElement">
                <Input
                  id="name"
                  type="text"
                  value={temporaryName}
                  placeholder={t`Enter a name`}
                  onChange={(e) => setTemporaryName(e.target.value)}
                  required
                  disabled={locked}
                />
                <Select
                  id="category"
                  value={temporaryCategory}
                  onChange={(e) => setTemporaryCategory(e.target.value)}
                  required
                  disabled={locked}
                >
                  {getCategoriesForSaving().map((category, index) => (
                    <option
                      key={category}
                      value={category} // Se è il placeholder, imposta il valore a stringa vuota
                      disabled={locked} // Disabilita il placeholder
                    >
                      {getTranslation(category)}
                    </option>
                  ))}
                </Select>
              </div>
              <progress
                style={{
                  width: "98%",
                  "accent-color":
                    temporaryDescription.length >= 120 ? "green" : "red",
                }}
                value={temporaryDescription.length / 120}
              >
                {temporaryDescription.length}
              </progress>
              <textarea
                disabled={locked}
                className="story-description"
                onChange={optimisedHandleChange}
                defaultValue={temporaryDescription}
              ></textarea>
              <div className="uploadSaveElement">
                <Button onClick={() => handleSubmissionUpdate()}>
                  {t`Update`}
                </Button>
                <Button onClick={() => handleCancel()}>{t`Cancel`}</Button>
              </div>
            </>
          )
        )}
      </div>
    </>
  );
});
export default DrawerContent;
